import { useRef, useState } from "react";
import { Trash2, ZoomIn, Scissors } from "lucide-react";
import { BUSES } from "@/lib/audioEngine";
import { shiftKfs } from "@/lib/layerMotion";
import { shiftPts, audOf } from "@/lib/clipAudio";
import { VolumeLine } from "@/components/editor/VolumeLine";

export const TRACKS = [
  { id: "fx", label: "V2 Layers", color: "#EC4899" },
  { id: "video", label: "V1 Picture", color: "#0284C7" },
  { id: "audio", label: "A1 Music", color: "#0D9488" },
  { id: "sfx", label: "A2 SFX", color: "#8B5CF6" },
  { id: "score", label: "A3 Score", color: "#D97706" },
];
const TRANS_LABEL = { dissolve: "Cross dissolve", dip: "Dip to black", wipeleft: "Wipe left", wiperight: "Wipe right", slideleft: "Slide left", slideright: "Slide right", flash: "Celestial flash", leak: "Golden light leak", flare: "Lens-flare sweep", starburst: "Starburst bloom" };
const TRANS_SHORT = { dissolve: "XD", dip: "DIP", wipeleft: "W◀", wiperight: "W▶", slideleft: "S◀", slideright: "S▶", flash: "✦", leak: "LEAK", flare: "FLR", starburst: "✸" };
const TRANS_BG = { dissolve: "repeating-linear-gradient(45deg,#fff8 0 4px,#FFB800 4px 8px)", dip: "repeating-linear-gradient(45deg,#000 0 4px,#FFB800 4px 8px)",
  flash: "linear-gradient(90deg,#FFB80055,#FFF2D1,#FFB80055)", leak: "linear-gradient(90deg,#FF9E38,#FFD27A,#FF9E38)", flare: "linear-gradient(90deg,#BFE0FF,#fff,#BFE0FF)", starburst: "radial-gradient(circle,#fff,#FFB800)", wipeleft: "linear-gradient(270deg,#FFB800,#0284C7)", wiperight: "linear-gradient(90deg,#FFB800,#0284C7)",
  slideleft: "repeating-linear-gradient(90deg,#FFB800 0 3px,#0284C7 3px 6px)", slideright: "repeating-linear-gradient(90deg,#FFB800 0 3px,#0284C7 3px 6px)" };
const layerRows = (fx) => {
  const ends = [], row = {};
  fx.forEach((c) => {
    let r = ends.findIndex((e) => e <= c.start + 1e-3);
    if (r < 0) r = ends.length;
    ends[r] = c.start + c.len; row[c.id] = r;
  });
  return { row, n: Math.max(1, ends.length) };
};
const rowStyle = ({ row, n }, c) => {
  if (row[c.id] === undefined) return { top: 4, bottom: 4 };
  const h = 40 / n;
  return { top: 4 + (n - 1 - row[c.id]) * h, height: h - (n > 1 ? 1 : 0) };
};
const busOf = (t) => (t === "video" ? "footage" : t);

export const Timeline = ({ clips, assetsMap, fullDuration, duration, time, onSeek, onChange, onReorder, onSplit, onRemove, mixer, setMixer, selected, setSelected }) => {
  const [pps, setPps] = useState(40);
  const drag = useRef(null);
  const width = Math.max(duration * pps + 200, 800);
  const step = pps > 80 ? 1 : pps > 30 ? 5 : 10;
  const rows = layerRows(clips.filter((c) => c.track === "fx"));

  const begin = (e, c, mode) => {
    e.stopPropagation();
    setSelected(c.id);
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { mode, x: e.clientX, c: { ...c }, idx: -1 };
  };
  const dragMove = (e) => {
    if (!drag.current) return;
    const { mode, x, c } = drag.current;
    const dx = (e.clientX - x) / pps;
    if (mode === "move" && c.track === "video") {
      const center = c.start + dx + c.len / 2;
      let acc = 0, idx = 0;
      clips.filter((o) => o.track === "video" && o.id !== c.id).forEach((o) => { if (center > acc + o.len / 2) idx++; acc += o.len; });
      if (idx !== drag.current.idx) { drag.current.idx = idx; onReorder(c.id, idx); }
    } else if (mode === "move") onChange(c.id, { start: Math.max(0, c.start + dx) });
    else if (mode === "trim-l") {
      const inn = Math.min(Math.max(0, c.in + dx), c.out - 0.1);
      onChange(c.id, c.track === "video" ? { in: inn, ...(c.aud && { aud: shiftPts(audOf(c), c.in - inn) }) } : { in: inn, start: Math.max(0, c.start + inn - c.in), ...(c.fx && { fx: { ...c.fx, ...shiftKfs(c.fx, c.in - inn) } }) });
    } else onChange(c.id, { out: Math.max(c.in + 0.1, Math.min(fullDuration(c), c.out + dx)) });
  };
  const end = () => (drag.current = null);

  const seekFrom = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    onSeek((e.clientX - r.left) / pps);
  };
  const toggle = (track, key) => {
    const b = busOf(track);
    setMixer({ ...mixer, buses: { ...mixer.buses, [b]: { ...mixer.buses[b], [key]: !mixer.buses[b][key] } } });
  };

  return (
    <div className="flex flex-col h-full" tabIndex={0} onKeyDown={(e) => (e.key === "Delete" || e.key === "Backspace") && selected && (onRemove(selected), setSelected(null))} data-testid="timeline">
      <div className="flex items-center justify-between px-3 h-8 border-b border-[#2A2E3D] shrink-0">
        <span className="text-[10px] font-mono-ui text-slate-500">{clips.length} CLIPS · DRAG TO MOVE / REORDER · EDGES TO TRIM · B TO BLADE</span>
        <div className="flex items-center gap-3">
          <button onClick={() => onSplit(selected)} className="text-xs text-slate-300 hover:text-[#FFB800] flex items-center gap-1" data-testid="timeline-split-clip"><Scissors className="w-3 h-3" />Blade at playhead</button>
          {selected && <button onClick={() => { onRemove(selected); setSelected(null); }} className="text-xs text-red-400 flex items-center gap-1" data-testid="timeline-remove-clip"><Trash2 className="w-3 h-3" />Remove clip</button>}
          <ZoomIn className="w-3.5 h-3.5 text-slate-500" />
          <input type="range" min={8} max={200} value={pps} onChange={(e) => setPps(+e.target.value)} className="mini w-28" data-testid="timeline-zoom" />
        </div>
      </div>
      <div className="flex flex-1 min-h-0 overflow-y-auto">
        <div className="w-32 shrink-0 border-r border-[#2A2E3D] bg-[#13151C]">
          <div className="h-6 border-b border-[#2A2E3D]" />
          {TRACKS.map((t) => {
            const b = mixer.buses[busOf(t.id)];
            return (
              <div key={t.id} className={`${t.id === "video" ? "h-20" : "h-12"} border-b border-[#2A2E3D] px-2 flex items-center justify-between`} data-testid={`track-header-${t.id}`}>
                <span className="text-[11px] font-medium flex items-center gap-1.5"><span className="w-1.5 h-4 rounded-sm" style={{ background: t.color }} />{t.label}</span>
                {b ? <div className="flex gap-0.5">
                  <button onClick={() => toggle(t.id, "mute")} className={`w-5 h-5 text-[9px] font-bold rounded ${b.mute ? "bg-red-500" : "bg-[#0B0C10] text-slate-500"}`} data-testid={`track-mute-${t.id}`}>M</button>
                  <button onClick={() => toggle(t.id, "solo")} className={`w-5 h-5 text-[9px] font-bold rounded ${b.solo ? "bg-[#FFB800] text-black" : "bg-[#0B0C10] text-slate-500"}`} data-testid={`track-solo-${t.id}`}>S</button>
                </div> : <span className="font-mono-ui text-[9px] text-slate-500">COMP</span>}
              </div>
            );
          })}
        </div>
        <div className="flex-1 overflow-x-auto overflow-y-hidden self-start">
          <div className="relative min-w-full" style={{ width }}>
            <div className="h-6 border-b border-[#2A2E3D] relative cursor-pointer" onPointerDown={seekFrom} data-testid="timeline-ruler">
              {Array.from({ length: Math.ceil(width / pps / step) }, (_, i) => (
                <span key={i} className="absolute top-0 h-full border-l border-[#2A2E3D] pl-1 text-[9px] font-mono-ui text-slate-500" style={{ left: i * step * pps }}>{i * step}s</span>
              ))}
            </div>
            {TRACKS.map((t) => (
              <div key={t.id} className={`${t.id === "video" ? "h-20" : "h-12"} border-b border-[#2A2E3D] relative bg-[#0B0C10]/40`} onPointerDown={(e) => e.target === e.currentTarget && (setSelected(null), seekFrom(e))} data-testid={`track-lane-${t.id}`}>
                {clips.filter((c) => c.track === t.id).map((c) => (
                  <div key={c.id} data-testid={`clip-${c.id}`}
                    className={`absolute rounded text-[10px] flex ${c.track === "video" ? "items-start pt-0.5" : "items-center"} overflow-hidden whitespace-nowrap select-none cursor-grab active:cursor-grabbing border ${selected === c.id ? "border-white" : "border-black/50"}`}
                    style={{ left: c.start * pps, width: Math.max(c.len * pps, 12), background: `${t.color}cc`, ...rowStyle(rows, c) }}
                    onPointerDown={(e) => begin(e, c, "move")} onPointerMove={dragMove} onPointerUp={end}>
                    <span className="absolute left-0 inset-y-0 w-2 cursor-ew-resize hover:bg-white/50 z-30" onPointerDown={(e) => begin(e, c, "trim-l")} data-testid={`clip-trim-left-${c.id}`} />
                    <span className="px-2.5 truncate pointer-events-none">{assetsMap[c.asset_id]?.name || "Missing media"}</span>
                    {c.track === "video" && <VolumeLine clip={c} pps={pps} active={selected === c.id} onSelect={() => setSelected(c.id)} onChange={(patch) => onChange(c.id, patch)} />}
                    {c.track === "fx" && (c.fx.kfs || []).map((k, i) => (
                      <button key={i} onPointerDown={(e) => { e.stopPropagation(); setSelected(c.id); onSeek(c.start + Math.min(k.t, c.len - 0.01)); }} data-testid={`kf-diamond-${c.id}-${i}`}
                        title={`Keyframe ${i + 1} · ${k.t.toFixed(2)}s`}
                        className={`absolute bottom-0.5 w-2 h-2 -ml-1 rotate-45 z-40 border border-black/60 ${Math.abs(c.start + k.t - time) < 0.02 ? "bg-[#FFB800]" : "bg-white"}`}
                        style={{ left: Math.max(5, Math.min(k.t * pps, c.len * pps - 5)) }} />
                    ))}
                    <span className="absolute right-0 inset-y-0 w-2 cursor-ew-resize hover:bg-white/50 z-30" onPointerDown={(e) => begin(e, c, "trim-r")} data-testid={`clip-trim-right-${c.id}`} />
                  </div>
                ))}
                {t.id === "video" && clips.filter((c) => c.track === "video" && c.transDur > 0).map((c) => (
                  <div key={`tr-${c.id}`} data-testid={`transition-marker-${c.id}`} title={TRANS_LABEL[c.trans.type]}
                    className="absolute top-0 h-3 z-20 border-x border-white/70 pointer-events-none"
                    style={{ left: c.start * pps, width: Math.max(c.transDur * pps, 8), background: TRANS_BG[c.trans.type] || TRANS_BG.dissolve }}>
                    <button onPointerDown={(e) => { e.stopPropagation(); setSelected(c.id); }} data-testid={`transition-marker-select-${c.id}`}
                      className="absolute left-1/2 top-0 -translate-x-1/2 px-1 h-3 rounded-b bg-black/80 text-[7px] font-bold text-[#FFB800] pointer-events-auto leading-3">
                      {TRANS_SHORT[c.trans.type]}
                    </button>
                  </div>
                ))}
              </div>
            ))}
            <div className="absolute top-0 bottom-0 w-px bg-[#FFB800] pointer-events-none shadow-[0_0_8px_#FFB800]" style={{ left: time * pps }} data-testid="timeline-playhead">
              <div className="w-2.5 h-2.5 -ml-[5px] bg-[#FFB800] rotate-45 -mt-1" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { BUSES };
